import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const SearchableSelect = ({ options = [], value, onChange, placeholder = "Select…", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => String(o.value) === String(value));

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(p => !p); setSearch(""); } }}
        style={{
          width: "100%", padding: "9px 13px",
          border: `1.5px solid ${open ? "var(--primary)" : "var(--border)"}`,
          borderRadius: "var(--radius-sm)", fontSize: 14,
          background: disabled ? "#f1f5f9" : open ? "var(--surface)" : "var(--surface-2)",
          color: selected ? "var(--text)" : "var(--text-light)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          boxShadow: open ? "0 0 0 3px rgba(79,70,229,0.1)" : "none",
          transition: "all 0.18s",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <i className={`bi bi-chevron-${open ? "up" : "down"}`} style={{ fontSize: 11, flexShrink: 0, marginLeft: 6, color: "var(--text-muted)" }} />
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 999,
          background: "#fff", border: "1.5px solid var(--border)",
          borderRadius: "var(--radius-sm)", boxShadow: "var(--shadow-lg)", overflow: "hidden",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)" }}>
            <input
              autoFocus
              type="text"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: "100%", border: "1.5px solid var(--border)", borderRadius: 6,
                padding: "5px 9px", fontSize: 13, outline: "none", fontFamily: "inherit",
              }}
              onFocus={e => { e.target.style.borderColor = "var(--primary)"; e.target.style.boxShadow = "0 0 0 3px rgba(79,70,229,0.1)"; }}
              onBlur={e => { e.target.style.borderColor = "var(--border)"; e.target.style.boxShadow = "none"; }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto", scrollbarWidth: "thin" }}>
            <div
              onClick={() => { onChange(""); setOpen(false); }}
              style={{ padding: "8px 13px", cursor: "pointer", fontSize: 13.5, color: "var(--text-light)" }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {placeholder}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 13px", fontSize: 13, color: "var(--text-light)", textAlign: "center" }}>No results</div>
            ) : filtered.map(o => {
              const isSelected = String(o.value) === String(value);
              return (
                <div
                  key={o.value}
                  onClick={() => { onChange(String(o.value)); setOpen(false); }}
                  style={{
                    padding: "8px 13px", cursor: "pointer", fontSize: 13.5,
                    background: isSelected ? "var(--primary-bg)" : "transparent",
                    color: isSelected ? "var(--primary)" : "var(--text)",
                    fontWeight: isSelected ? 600 : 400,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "var(--surface-2)"; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  {o.label}
                  {isSelected && <i className="bi bi-check2" style={{ color: "var(--primary)", fontSize: 14 }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const FIELD_TYPES = [
  "text",
  "select",
  "textarea",
  "number",
  "date",
  "email",
  "phone",
  "url",
  "checkbox",
  "radio",
  "datetime-local",
  "time",
  "file",
  "currency",
  "percentage",
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  :root {
    --primary: #4f46e5;
    --primary-light: #818cf8;
    --primary-bg: #eef2ff;
    --surface: #ffffff;
    --surface-2: #f8fafc;
    --border: #e2e8f0;
    --border-focus: #4f46e5;
    --text: #0f172a;
    --text-muted: #64748b;
    --text-light: #94a3b8;
    --danger: #ef4444;
    --danger-bg: #fef2f2;
    --success: #10b981;
    --warning: #f59e0b;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
    --shadow-md: 0 4px 16px rgba(79,70,229,0.08), 0 2px 4px rgba(0,0,0,0.04);
    --shadow-lg: 0 8px 32px rgba(79,70,229,0.12), 0 4px 8px rgba(0,0,0,0.06);
    --radius: 14px;
    --radius-sm: 8px;
  }

  .afm-wrap * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; }

  .afm-wrap {
    background: linear-gradient(135deg, #f0f4ff 0%, #fafafe 50%, #f8f0ff 100%);
    min-height: 100vh;
  }

  /* ── Header ── */
  .afm-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 18px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  .afm-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .afm-icon-badge {
    width: 46px; height: 46px;
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(79,70,229,0.3);
  }
  .afm-icon-badge i { color: #fff; font-size: 20px; }
  .afm-title { font-size: 20px; font-weight: 700; color: var(--text); margin: 0; letter-spacing: -0.3px; }
  .afm-subtitle { font-size: 13px; color: var(--text-muted); margin: 2px 0 0; }
  .afm-back-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: var(--radius-sm);
    background: var(--surface-2); border: 1px solid var(--border);
    font-size: 13px; font-weight: 600; color: var(--text-muted);
    cursor: pointer; transition: all 0.18s ease;
    text-decoration: none;
  }
  .afm-back-btn:hover { background: var(--primary-bg); border-color: var(--primary-light); color: var(--primary); }

  /* ── Content ── */
  .afm-content { padding: 28px; }

  /* ── Tabs ── */
  .afm-tabs {
    display: flex; gap: 4px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px; padding: 5px;
    width: fit-content; margin-bottom: 28px;
    box-shadow: var(--shadow-sm);
  }
  .afm-tab-btn {
    display: flex; align-items: center; gap: 8px;
    padding: 9px 20px; border-radius: 9px; border: none;
    font-size: 13.5px; font-weight: 600; cursor: pointer;
    transition: all 0.18s ease; color: var(--text-muted);
    background: transparent;
  }
  .afm-tab-btn:hover { color: var(--text); background: var(--surface-2); }
  .afm-tab-btn.active {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: #fff; box-shadow: 0 3px 10px rgba(79,70,229,0.35);
  }
  .afm-tab-btn i { font-size: 15px; }
  .afm-tab-count {
    background: rgba(255,255,255,0.25); color: #fff;
    font-size: 11px; font-weight: 700; padding: 1px 7px;
    border-radius: 20px; min-width: 22px; text-align: center;
  }
  .afm-tab-btn:not(.active) .afm-tab-count {
    background: var(--primary-bg); color: var(--primary);
  }

  /* ── Cards ── */
  .afm-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-md);
    overflow: hidden;
  }
  .afm-card-header {
    padding: 18px 22px 16px;
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 10px;
  }
  .afm-card-icon {
    width: 34px; height: 34px;
    background: var(--primary-bg); border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
  }
  .afm-card-icon i { color: var(--primary); font-size: 16px; }
  .afm-card-title { font-size: 15px; font-weight: 700; color: var(--text); margin: 0; }
  .afm-card-body { padding: 22px; }

  /* ── Form elements ── */
  .afm-label {
    display: block; font-size: 12.5px; font-weight: 600;
    color: var(--text-muted); margin-bottom: 6px; letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  .afm-label .req { color: var(--danger); margin-left: 2px; }
  .afm-input, .afm-select, .afm-textarea {
    width: 100%; padding: 9px 13px;
    border: 1.5px solid var(--border); border-radius: var(--radius-sm);
    font-size: 14px; font-family: 'Plus Jakarta Sans', sans-serif;
    color: var(--text); background: var(--surface-2);
    transition: all 0.18s ease; outline: none;
  }
  .afm-input:focus, .afm-select:focus, .afm-textarea:focus {
    border-color: var(--primary); background: var(--surface);
    box-shadow: 0 0 0 3px rgba(79,70,229,0.1);
  }
  .afm-select { cursor: pointer; }
  .afm-textarea { resize: vertical; min-height: 110px; }
  .afm-check-wrap {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 13px; border: 1.5px solid var(--border);
    border-radius: var(--radius-sm); background: var(--surface-2);
    cursor: pointer; transition: all 0.18s ease;
  }
  .afm-check-wrap:hover { border-color: var(--primary-light); background: var(--primary-bg); }
  .afm-check-wrap input[type=checkbox] { width: 16px; height: 16px; accent-color: var(--primary); cursor: pointer; }
  .afm-check-label { font-size: 14px; font-weight: 500; color: var(--text); cursor: pointer; }

  /* ── Buttons ── */
  .afm-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px; border-radius: var(--radius-sm); border: none;
    font-size: 13.5px; font-weight: 600; cursor: pointer;
    transition: all 0.18s ease; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .afm-btn-primary {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: #fff; box-shadow: 0 3px 10px rgba(79,70,229,0.3);
  }
  .afm-btn-primary:hover { box-shadow: 0 5px 16px rgba(79,70,229,0.4); transform: translateY(-1px); }
  .afm-btn-primary:disabled { opacity: 0.6; transform: none; cursor: not-allowed; }
  .afm-btn-ghost {
    background: var(--surface-2); color: var(--text-muted);
    border: 1.5px solid var(--border);
  }
  .afm-btn-ghost:hover { background: var(--surface); color: var(--text); border-color: #cbd5e1; }

  /* ── Filter bar ── */
  .afm-filter-bar {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 16px; flex-wrap: wrap;
  }
  .afm-filter-label { font-size: 12.5px; font-weight: 600; color: var(--text-muted); white-space: nowrap; }
  .afm-filter-select {
    padding: 7px 12px; border: 1.5px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: var(--text); background: var(--surface-2);
    cursor: pointer; outline: none; transition: all 0.18s;
  }
  .afm-filter-select:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }

  /* ── Table ── */
  .afm-table-wrap { overflow-x: auto; }
  .afm-table {
    width: 100%; border-collapse: collapse;
    font-size: 13.5px;
  }
  .afm-table thead tr {
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
    border-bottom: 2px solid var(--border);
  }
  .afm-table th {
    padding: 11px 14px; text-align: left;
    font-size: 11.5px; font-weight: 700;
    color: var(--text-muted); letter-spacing: 0.06em;
    text-transform: uppercase; white-space: nowrap;
  }
  .afm-table td { padding: 12px 14px; color: var(--text); vertical-align: middle; }
  .afm-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.12s;
  }
  .afm-table tbody tr:last-child { border-bottom: none; }
  .afm-table tbody tr:hover { background: #fafbff; }

  /* ── Scrollable list body ── */
  .afm-list-scroll {
    max-height: 480px;
    overflow-y: auto;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }
  .afm-list-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
  .afm-list-scroll::-webkit-scrollbar-track { background: transparent; }
  .afm-list-scroll::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 99px;
  }
  .afm-list-scroll::-webkit-scrollbar-thumb:hover { background: var(--primary-light); }

  /* Sticky thead inside scrollable list */
  .afm-list-scroll .afm-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  }

  /* ── Badges ── */
  .afm-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 11.5px; font-weight: 600;
  }
  .afm-badge-type {
    background: var(--primary-bg); color: var(--primary);
  }
  .afm-badge-cat {
    background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
  }
  .afm-badge-req {
    background: var(--danger-bg); color: var(--danger); border: 1px solid #fecaca;
  }
  .afm-badge-opt {
    background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
  }
  .afm-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px; color: #7c3aed;
    background: #faf5ff; padding: 2px 8px;
    border-radius: 5px; border: 1px solid #e9d5ff;
  }

  /* ── Action buttons ── */
  .afm-action-btn {
    width: 30px; height: 30px; border-radius: 7px; border: none;
    display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s; font-size: 13px;
  }
  .afm-action-edit { background: var(--primary-bg); color: var(--primary); }
  .afm-action-edit:hover { background: var(--primary); color: #fff; }
  .afm-action-del { background: var(--danger-bg); color: var(--danger); }
  .afm-action-del:hover { background: var(--danger); color: #fff; }
  .afm-action-toggle-on { background: rgba(16,185,129,0.1); color: var(--success); border: 1.5px solid rgba(16,185,129,0.3); }
  .afm-action-toggle-on:hover { background: var(--success); color: #fff; }
  .afm-action-toggle-off { background: rgba(148,163,184,0.1); color: var(--text-light); border: 1.5px solid rgba(148,163,184,0.25); }
  .afm-action-toggle-off:hover { background: rgba(79,70,229,0.08); color: var(--primary); border-color: var(--primary-light); }

  /* ── Empty state ── */
  .afm-empty {
    text-align: center; padding: 48px 20px;
    color: var(--text-muted);
  }
  .afm-empty-icon {
    width: 60px; height: 60px;
    background: var(--surface-2); border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 14px; border: 1.5px dashed var(--border);
  }
  .afm-empty-icon i { font-size: 24px; color: var(--text-light); }
  .afm-empty-text { font-size: 14px; font-weight: 500; color: var(--text-muted); margin: 0; }
  .afm-empty-sub { font-size: 12.5px; color: var(--text-light); margin: 4px 0 0; }

  /* ── Row number ── */
  .afm-row-num {
    width: 24px; height: 24px; border-radius: 6px;
    background: var(--surface-2); border: 1px solid var(--border);
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 11.5px; font-weight: 700; color: var(--text-muted);
  }

  /* ── Options preview ── */
  .afm-options-preview {
    font-size: 12px; color: var(--text-muted);
    max-width: 180px; overflow: hidden;
    text-overflow: ellipsis; white-space: nowrap;
  }

  /* ── Options hint ── */
  .afm-hint { font-size: 11.5px; color: var(--text-light); margin-top: 5px; }

  /* ── Form group ── */
  .afm-form-group { margin-bottom: 16px; }

  /* ── Two col row ── */
  .afm-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  /* ── Separator ── */
  .afm-sep { height: 1px; background: var(--border); margin: 4px 0 18px; }

  /* ── Info row in header ── */
  .afm-info-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 12px; border-radius: 20px;
    background: var(--primary-bg); border: 1px solid #c7d2fe;
    font-size: 12px; font-weight: 600; color: var(--primary);
  }

  @media (max-width: 768px) {
    .afm-content { padding: 16px; }
    .afm-tabs { width: 100%; }
    .afm-tab-btn { flex: 1; justify-content: center; padding: 9px 10px; font-size: 12px; }
    .afm-row-2 { grid-template-columns: 1fr; }
    .afm-list-scroll { max-height: 320px; }
  }

  /* ── Loading spinner ── */
  .afm-spinner-wrap {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 48px 20px; gap: 14px;
  }
  .afm-spinner {
    width: 36px; height: 36px;
    border: 3px solid var(--primary-bg);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: afm-spin 0.7s linear infinite;
  }
  @keyframes afm-spin { to { transform: rotate(360deg); } }
  .afm-spinner-text { font-size: 13px; font-weight: 500; color: var(--text-muted); }

  /* ── Confirm Modal ── */
  .afm-modal-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(15,23,42,0.45);
    backdrop-filter: blur(3px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
  }
  .afm-modal-dialog {
    background: var(--surface);
    border-radius: var(--radius);
    box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    width: 100%; max-width: 420px;
    overflow: hidden;
    animation: afm-modal-in 0.18s ease;
  }
  @keyframes afm-modal-in {
    from { opacity: 0; transform: scale(0.94) translateY(-8px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  .afm-modal-header {
    padding: 18px 22px 14px;
    display: flex; align-items: center; gap: 12px;
    border-bottom: 1px solid var(--border);
  }
  .afm-modal-icon {
    width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 18px;
  }
  .afm-modal-icon-danger { background: var(--danger-bg); color: var(--danger); }
  .afm-modal-icon-warning { background: #fffbeb; color: var(--warning); }
  .afm-modal-title { font-size: 15px; font-weight: 700; color: var(--text); margin: 0; }
  .afm-modal-body { padding: 16px 22px 20px; font-size: 13.5px; color: var(--text-muted); line-height: 1.6; }
  .afm-modal-footer {
    padding: 0 22px 18px;
    display: flex; justify-content: flex-end; gap: 8px;
  }
  .afm-btn-danger {
    background: var(--danger); color: #fff;
    box-shadow: 0 3px 10px rgba(239,68,68,0.3);
  }
  .afm-btn-danger:hover { background: #dc2626; box-shadow: 0 5px 16px rgba(239,68,68,0.4); transform: translateY(-1px); }
  .afm-btn-warning {
    background: var(--warning); color: #fff;
    box-shadow: 0 3px 10px rgba(245,158,11,0.3);
  }
  .afm-btn-warning:hover { background: #d97706; transform: translateY(-1px); }
`;

export default function AssetFieldMasterPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [activeTab, setActiveTab] = useState("categories");

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [fields, setFields] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeSubCategories, setActiveSubCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  const [selectedCatId, setSelectedCatId] = useState("");
  const [selectedSubId, setSelectedSubId] = useState("");

  const [catForm, setCatForm] = useState({ name: "", sortOrder: 0 });
  const [catEditId, setCatEditId] = useState(null);

  const [subForm, setSubForm] = useState({ name: "", categoryId: "", sortOrder: 0 });
  const [subEditId, setSubEditId] = useState(null);

  const [fieldForm, setFieldForm] = useState({
    fieldName: "", label: "", fieldType: "text", required: false,
    sortOrder: 0, subCategoryId: "", optionsRaw: "",
  });
  const [fieldEditId, setFieldEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", variant: "danger", onConfirm: null });
  const openConfirm = (title, message, variant, onConfirmFn) =>
    setConfirmModal({ open: true, title, message, variant, onConfirm: onConfirmFn });
  const closeConfirm = () => setConfirmModal((p) => ({ ...p, open: false, onConfirm: null }));

  useEffect(() => {
    const bs = document.createElement("link");
    bs.rel = "stylesheet";
    bs.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bs);
    const bi = document.createElement("link");
    bi.rel = "stylesheet";
    bi.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(bi);
    return () => { bs.remove(); bi.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); return; }

    // Check page-level permission — deny only when explicitly false
    const empId = localStorage.getItem("employeeId");
    fetch(`${API_BASE_URL}/api/permissions/${empId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.pages?.asset_field_master === false) {
          toast.error("You don't have access to Field Master.");
          navigate("/admin-assets");
        }
      })
      .catch(() => {});
  }, [navigate, token]);

  const get = useCallback(async (path) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [token]);

  const mutate = useCallback(async (method, path, body) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      ...(body != null && { body: JSON.stringify(body) }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [token]);

  const fetchCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const [all, active] = await Promise.all([
        get("/api/asset-masters/categories?includeAll=true"),
        get("/api/asset-masters/categories"),
      ]);
      setCategories(all);
      setActiveCategories(active);
    } catch { setCategories([]); setActiveCategories([]); }
    finally { setLoadingCats(false); }
  }, [get]);

  const fetchSubCategories = useCallback(async (catId) => {
    setLoadingSubs(true);
    try {
      const allUrl = catId
        ? `/api/asset-masters/subcategories?categoryId=${catId}&includeAll=true`
        : "/api/asset-masters/subcategories?includeAll=true";
      const activeUrl = catId
        ? `/api/asset-masters/subcategories?categoryId=${catId}`
        : "/api/asset-masters/subcategories";
      const [all, active] = await Promise.all([get(allUrl), get(activeUrl)]);
      setSubCategories(all);
      setActiveSubCategories(active);
    } catch { setSubCategories([]); setActiveSubCategories([]); }
    finally { setLoadingSubs(false); }
  }, [get]);

  const fetchFields = useCallback(async (subId) => {
    if (!subId) { setFields([]); return; }
    setLoadingFields(true);
    try { setFields(await get(`/api/asset-masters/fields?subCategoryId=${subId}&includeAll=true`)); }
    catch { setFields([]); }
    finally { setLoadingFields(false); }
  }, [get]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchSubCategories(selectedCatId); }, [fetchSubCategories, selectedCatId]);
  useEffect(() => { fetchFields(selectedSubId); }, [fetchFields, selectedSubId]);

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const submitCategory = async (e) => {
    e.preventDefault();
    if (!catForm.name.trim()) { toast.error("Category name is required."); return; }
    try {
      setSubmitting(true);
      if (catEditId) {
        await mutate("PUT", `/api/asset-masters/categories/${catEditId}`, catForm);
        toast.success("Category updated.");
      } else {
        await mutate("POST", "/api/asset-masters/categories", catForm);
        toast.success("Category created.");
      }
      setCatForm({ name: "", sortOrder: 0 }); setCatEditId(null);
      fetchCategories();
    } catch (err) { toast.error(err.message || "Failed to save category."); }
    finally { setSubmitting(false); }
  };

  const editCategory = (c) => { setCatEditId(c.id); setCatForm({ name: c.name, sortOrder: c.sortOrder }); };
  const deleteCategory = (id) => {
    openConfirm(
      "Deactivate Category",
      "This will hide the category from the asset form. You can reactivate it later by editing the record.",
      "warning",
      async () => {
        try { await mutate("DELETE", `/api/asset-masters/categories/${id}`); toast.success("Category deactivated."); fetchCategories(); }
        catch (err) { toast.error("You dont have Permission to delete or Something went wrong"); }
      }
    );
  };

  const submitSubCategory = async (e) => {
    e.preventDefault();
    if (!subForm.name.trim()) { toast.error("Name is required."); return; }
    if (!subForm.categoryId) { toast.error("Select a category."); return; }
    try {
      setSubmitting(true);
      if (subEditId) {
        await mutate("PUT", `/api/asset-masters/subcategories/${subEditId}`, subForm);
        toast.success("Sub-category updated.");
      } else {
        await mutate("POST", "/api/asset-masters/subcategories", subForm);
        toast.success("Sub-category created.");
      }
      setSubForm({ name: "", categoryId: subForm.categoryId, sortOrder: 0 }); setSubEditId(null);
      fetchSubCategories(selectedCatId);
    } catch (err) { toast.error(err.message || "Failed to save sub-category."); }
    finally { setSubmitting(false); }
  };

  const editSubCategory = (s) => {
    setSubEditId(s.id);
    setSubForm({ name: s.name, categoryId: s.category?.id || "", sortOrder: s.sortOrder });
  };
  const deleteSubCategory = (id) => {
    openConfirm(
      "Deactivate Sub-Category",
      "This will hide the sub-category from the asset form. You can reactivate it later by editing the record.",
      "warning",
      async () => {
        try { await mutate("DELETE", `/api/asset-masters/subcategories/${id}`); toast.success("Sub-category deactivated."); fetchSubCategories(selectedCatId); }
        catch (err) { toast.error(err.message); }
      }
    );
  };

  const toggleCategoryActive = async (c) => {
    try {
      await mutate("PUT", `/api/asset-masters/categories/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Category deactivated." : "Category reactivated.");
      fetchCategories();
    } catch (err) { toast.error(err.message); }
  };

  const toggleSubCategoryActive = async (s) => {
    try {
      await mutate("PUT", `/api/asset-masters/subcategories/${s.id}`, { active: !s.active });
      toast.success(s.active ? "Sub-category deactivated." : "Sub-category reactivated.");
      fetchSubCategories(selectedCatId);
    } catch (err) { toast.error(err.message); }
  };

  const submitField = async (e) => {
    e.preventDefault();
    if (!fieldForm.fieldName.trim()) { toast.error("Field name is required."); return; }
    if (!fieldForm.label.trim()) { toast.error("Label is required."); return; }
    if (!fieldForm.subCategoryId) { toast.error("Select a sub-category."); return; }
    const options = fieldForm.optionsRaw.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload = { ...fieldForm, options };
    try {
      setSubmitting(true);
      if (fieldEditId) {
        await mutate("PUT", `/api/asset-masters/fields/${fieldEditId}`, payload);
        toast.success("Field updated.");
      } else {
        await mutate("POST", "/api/asset-masters/fields", payload);
        toast.success("Field created.");
      }
      setFieldForm({ fieldName: "", label: "", fieldType: "text", required: false, sortOrder: 0, subCategoryId: fieldForm.subCategoryId, optionsRaw: "" });
      setFieldEditId(null);
      fetchFields(selectedSubId);
    } catch (err) { toast.error(err.message || "Failed to save field."); }
    finally { setSubmitting(false); }
  };

  const editField = (f) => {
    setFieldEditId(f.id);
    const optionsRaw = (f.options || []).map((o) => o.optionValue).join("\n");
    setFieldForm({
      fieldName: f.fieldName, label: f.label, fieldType: f.fieldType,
      required: f.required, sortOrder: f.sortOrder,
      subCategoryId: f.subCategoryId || selectedSubId, optionsRaw,
    });
  };
  const deleteField = (id) => {
    openConfirm(
      "Deactivate Field",
      "This will hide the field from the asset form. You can reactivate it using the eye toggle at any time.",
      "warning",
      async () => {
        try { await mutate("DELETE", `/api/asset-masters/fields/${id}`); toast.success("Field deactivated."); fetchFields(selectedSubId); }
        catch (err) { toast.error(err.message); }
      }
    );
  };

  const toggleFieldActive = async (f) => {
    try {
      await mutate("PUT", `/api/asset-masters/fields/${f.id}`, { active: !f.active });
      toast.success(f.active ? "Field hidden from asset form." : "Field visible in asset form.");
      fetchFields(selectedSubId);
    } catch (err) { toast.error(err.message); }
  };

  const deleteFieldPermanently = (f) => {
    openConfirm(
      `Delete "${f.label}"`,
      `Permanently delete the field "${f.label}" (${f.fieldName})? This cannot be undone — all options associated with this field will also be removed.`,
      "danger",
      async () => {
        try {
          await mutate("DELETE", `/api/asset-masters/fields/${f.id}/permanent`, null);
          toast.success(`"${f.label}" permanently deleted.`);
          fetchFields(selectedSubId);
        } catch (err) { toast.error("You dont have Permission to delete or Something went wrong"); }
      }
    );
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
 const typeIcon = (t) => ({
  text:           "bi-fonts",
  select:         "bi-list-ul",
  textarea:       "bi-textarea-resize",
  number:         "bi-hash",
  date:           "bi-calendar3",
  email:          "bi-envelope",
  phone:          "bi-telephone",
  url:            "bi-link-45deg",
  checkbox:       "bi-check2-square",
  radio:          "bi-record-circle",
  "datetime-local": "bi-calendar-event",
  time:           "bi-clock",
  file:           "bi-paperclip",
  currency:       "bi-currency-rupee",   // swap to bi-currency-dollar if needed
  percentage:     "bi-percent",
})[t] || "bi-question-circle";

  const TABS = [
    { key: "categories", icon: "bi-tag-fill", label: "Categories", count: categories.length },
    { key: "subcategories", icon: "bi-tags-fill", label: "Sub-Categories", count: subCategories.length },
    { key: "fields", icon: "bi-ui-checks-grid", label: "Fields", count: fields.filter(f => f.active).length },
  ];

  return (
    <>
      <style>{styles}</style>
      <ToastContainer position="top-right" autoClose={4000} />

      {/* ── Confirm Modal ── */}
      {confirmModal.open && (
        <div className="afm-modal-overlay" onClick={closeConfirm}>
          <div className="afm-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="afm-modal-header">
              <div className={`afm-modal-icon afm-modal-icon-${confirmModal.variant}`}>
                <i className={`bi ${confirmModal.variant === "danger" ? "bi-trash3-fill" : "bi-slash-circle-fill"}`}></i>
              </div>
              <div className="afm-modal-title">{confirmModal.title}</div>
            </div>
            <div className="afm-modal-body">{confirmModal.message}</div>
            <div className="afm-modal-footer">
              <button className="afm-btn afm-btn-ghost" onClick={closeConfirm}>Cancel</button>
              <button
                className={`afm-btn afm-btn-${confirmModal.variant}`}
                onClick={() => { confirmModal.onConfirm(); closeConfirm(); }}
              >
                <i className={`bi ${confirmModal.variant === "danger" ? "bi-trash3" : "bi-slash-circle"} me-1`}></i>
                {confirmModal.variant === "danger" ? "Delete Permanently" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed-top"><AssetModuleNavbar /></div>

      <div className="afm-wrap" style={{ paddingTop: "110px" }}>

        {/* ── Header ── */}
        <div className="afm-header">
          <div className="afm-header-left">
            <div className="afm-icon-badge">
              <i className="bi bi-database-fill"></i>
            </div>
            <div>
              <div className="afm-title">Asset Field Master</div>
              <div className="afm-subtitle">Manage categories, sub-categories & dynamic form fields</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="afm-info-pill">
              <i className="bi bi-layers-half"></i>
              {categories.length} Categories · {subCategories.length} Sub-cats
            </span>
            <button className="afm-back-btn" onClick={() => navigate("/allassets")}>
              <i className="bi bi-arrow-left"></i> Asset List
            </button>
          </div>
        </div>

        <div className="afm-content">

          {/* ── Tabs ── */}
          <div className="afm-tabs">
            {TABS.map(({ key, icon, label, count }) => (
              <button
                key={key}
                className={`afm-tab-btn ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <i className={`bi ${icon}`}></i>
                {label}
                <span className="afm-tab-count">{count}</span>
              </button>
            ))}
          </div>

          {/* ── TAB: Categories ── */}
          {activeTab === "categories" && (
            <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20 }}>

              {/* Form */}
              <div className="afm-card">
                <div className="afm-card-header">
                  <div className="afm-card-icon">
                    <i className={`bi ${catEditId ? "bi-pencil-square" : "bi-plus-lg"}`}></i>
                  </div>
                  <span className="afm-card-title">{catEditId ? "Edit Category" : "New Category"}</span>
                </div>
                <div className="afm-card-body">
                  <form onSubmit={submitCategory}>
                    <div className="afm-form-group">
                      <label className="afm-label">Name <span className="req">*</span></label>
                      <input className="afm-input" placeholder="e.g. IT Equipment"
                        value={catForm.name}
                        onChange={(e) => setCatForm((p) => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="afm-form-group">
                      <label className="afm-label">Sort Order</label>
                      <input type="number" className="afm-input" value={catForm.sortOrder}
                        onChange={(e) => setCatForm((p) => ({ ...p, sortOrder: +e.target.value }))} />
                    </div>
                    <div className="afm-sep" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="afm-btn afm-btn-primary" type="submit" disabled={submitting}>
                        <i className={`bi ${catEditId ? "bi-check2" : "bi-plus-lg"}`}></i>
                        {submitting ? "Saving…" : catEditId ? "Update" : "Create"}
                      </button>
                      <button className="afm-btn afm-btn-ghost" type="button"
                        onClick={() => { setCatEditId(null); setCatForm({ name: "", sortOrder: 0 }); }}>
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* List — scrollable */}
              <div className="afm-card">
                <div className="afm-card-header">
                  <div className="afm-card-icon"><i className="bi bi-list-ul"></i></div>
                  <span className="afm-card-title">Category List</span>
                  <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--text-muted)", fontWeight: 600 }}>
                    {categories.length} total
                  </span>
                </div>
                <div className="afm-list-scroll">
                  {loadingCats ? (
                    <div className="afm-spinner-wrap">
                      <div className="afm-spinner" />
                      <span className="afm-spinner-text">Loading categories…</span>
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="afm-empty">
                      <div className="afm-empty-icon"><i className="bi bi-tag"></i></div>
                      <p className="afm-empty-text">No categories yet</p>
                      <p className="afm-empty-sub">Create your first category using the form</p>
                    </div>
                  ) : (
                    <table className="afm-table">
                      <thead>
                        <tr>
                          <th>Sr.no</th>
                          <th>Name</th>
                          <th>Sort</th>
                          <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((c, i) => (
                          <tr key={c.id} style={{ opacity: c.active ? 1 : 0.5 }}>
                            <td><span className="afm-row-num">{i + 1}</span></td>
                            <td style={{ fontWeight: 600 }}>
                              {c.name}
                              {!c.active && <span className="ms-2" style={{ fontSize: 11, color: "var(--text-light)", fontWeight: 400 }}>(inactive)</span>}
                            </td>
                            <td style={{ color: "var(--text-muted)" }}>{c.sortOrder}</td>
                            <td style={{ textAlign: "right" }}>
                              <button className="afm-action-btn afm-action-edit me-1" onClick={() => editCategory(c)} title="Edit">
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className={`afm-action-btn afm-action-toggle${c.active ? "-on" : "-off"} me-1`}
                                onClick={() => toggleCategoryActive(c)}
                                title={c.active ? "Deactivate" : "Reactivate"}
                              >
                                <i className={`bi ${c.active ? "bi-eye-fill" : "bi-eye-slash-fill"}`}></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── TAB: Sub-Categories ── */}
          {activeTab === "subcategories" && (
            <div style={{ display: "grid", gridTemplateColumns: "400px 1fr", gap: 20 }}>

              {/* Form */}
              <div className="afm-card">
                <div className="afm-card-header">
                  <div className="afm-card-icon">
                    <i className={`bi ${subEditId ? "bi-pencil-square" : "bi-plus-lg"}`}></i>
                  </div>
                  <span className="afm-card-title">{subEditId ? "Edit Sub-Category" : "New Sub-Category"}</span>
                </div>
                <div className="afm-card-body">
                  <form onSubmit={submitSubCategory}>
                    <div className="afm-form-group">
                      <label className="afm-label">Category <span className="req">*</span></label>
                      <SearchableSelect
                        options={activeCategories.map(c => ({ value: c.id, label: c.name }))}
                        value={subForm.categoryId}
                        onChange={(v) => setSubForm((p) => ({ ...p, categoryId: v }))}
                        placeholder="Select category…"
                      />
                    </div>
                    <div className="afm-form-group">
                      <label className="afm-label">Name <span className="req">*</span></label>
                      <input className="afm-input" placeholder="e.g. Laptops"
                        value={subForm.name}
                        onChange={(e) => setSubForm((p) => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="afm-form-group">
                      <label className="afm-label">Sort Order</label>
                      <input type="number" className="afm-input" value={subForm.sortOrder}
                        onChange={(e) => setSubForm((p) => ({ ...p, sortOrder: +e.target.value }))} />
                    </div>
                    <div className="afm-sep" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="afm-btn afm-btn-primary" type="submit" disabled={submitting}>
                        <i className={`bi ${subEditId ? "bi-check2" : "bi-plus-lg"}`}></i>
                        {submitting ? "Saving…" : subEditId ? "Update" : "Create"}
                      </button>
                      <button className="afm-btn afm-btn-ghost" type="button"
                        onClick={() => { setSubEditId(null); setSubForm({ name: "", categoryId: "", sortOrder: 0 }); }}>
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* List — scrollable */}
              <div className="afm-card">
                <div className="afm-card-header">
                  <div className="afm-card-icon"><i className="bi bi-list-ul"></i></div>
                  <span className="afm-card-title">Sub-Category List</span>
                  <div className="afm-filter-bar" style={{ marginLeft: "auto", marginBottom: 0 }}>
                    <span className="afm-filter-label">Filter:</span>
                    <div style={{ minWidth: 180 }}>
                      <SearchableSelect
                        options={activeCategories.map(c => ({ value: c.id, label: c.name }))}
                        value={selectedCatId}
                        onChange={(v) => setSelectedCatId(v)}
                        placeholder="All categories"
                      />
                    </div>
                  </div>
                </div>
                <div className="afm-list-scroll">
                  {loadingSubs ? (
                    <div className="afm-spinner-wrap">
                      <div className="afm-spinner" />
                      <span className="afm-spinner-text">Loading sub-categories…</span>
                    </div>
                  ) : subCategories.length === 0 ? (
                    <div className="afm-empty">
                      <div className="afm-empty-icon"><i className="bi bi-tags"></i></div>
                      <p className="afm-empty-text">No sub-categories found</p>
                      <p className="afm-empty-sub">Try a different category filter or create a new one</p>
                    </div>
                  ) : (
                    <table className="afm-table">
                      <thead>
                        <tr>
                          <th>Sr.no</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Sort</th>
                          <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subCategories.map((s, i) => (
                          <tr key={s.id} style={{ opacity: s.active ? 1 : 0.5 }}>
                            <td><span className="afm-row-num">{i + 1}</span></td>
                            <td style={{ fontWeight: 600 }}>
                              {s.name}
                              {!s.active && <span className="ms-2" style={{ fontSize: 11, color: "var(--text-light)", fontWeight: 400 }}>(inactive)</span>}
                            </td>
                            <td><span className="afm-badge afm-badge-cat">{s.category?.name}</span></td>
                            <td style={{ color: "var(--text-muted)" }}>{s.sortOrder}</td>
                            <td style={{ textAlign: "right" }}>
                              <button className="afm-action-btn afm-action-edit me-1" onClick={() => editSubCategory(s)} title="Edit">
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className={`afm-action-btn afm-action-toggle${s.active ? "-on" : "-off"} me-1`}
                                onClick={() => toggleSubCategoryActive(s)}
                                title={s.active ? "Deactivate" : "Reactivate"}
                              >
                                <i className={`bi ${s.active ? "bi-eye-fill" : "bi-eye-slash-fill"}`}></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── TAB: Fields ── */}
          {activeTab === "fields" && (
            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 20 }}>

              {/* Form */}
              <div className="afm-card">
                <div className="afm-card-header">
                  <div className="afm-card-icon">
                    <i className={`bi ${fieldEditId ? "bi-pencil-square" : "bi-plus-lg"}`}></i>
                  </div>
                  <span className="afm-card-title">{fieldEditId ? "Edit Field" : "New Field"}</span>
                </div>
                <div className="afm-card-body">
                  <form onSubmit={submitField}>
                    <div className="afm-form-group">
                      <label className="afm-label">Category</label>
                      <SearchableSelect
                        options={activeCategories.map(c => ({ value: c.id, label: c.name }))}
                        value={selectedCatId}
                        onChange={(v) => { setSelectedCatId(v); setSelectedSubId(""); setFieldForm((p) => ({ ...p, subCategoryId: "" })); }}
                        placeholder="Select category…"
                      />
                    </div>
                    <div className="afm-form-group">
                      <label className="afm-label">Sub-Category <span className="req">*</span></label>
                      <SearchableSelect
                        options={activeSubCategories.map(s => ({ value: s.id, label: s.name }))}
                        value={fieldForm.subCategoryId}
                        disabled={!selectedCatId}
                        onChange={(v) => { setFieldForm((p) => ({ ...p, subCategoryId: v })); setSelectedSubId(v); }}
                        placeholder={selectedCatId ? "Select sub-category…" : "Select a category first"}
                      />
                    </div>

                    <div className="afm-sep" />

                    <div className="afm-row-2 afm-form-group">
                      <div>
                        <label className="afm-label">Field Name <span className="req">*</span></label>
                        <input className="afm-input" placeholder="camelCaseKey"
                          value={fieldForm.fieldName}
                          onChange={(e) => setFieldForm((p) => ({ ...p, fieldName: e.target.value }))} />
                      </div>
                      <div>
                        <label className="afm-label">Label <span className="req">*</span></label>
                        <input className="afm-input" placeholder="Display label"
                          value={fieldForm.label}
                          onChange={(e) => setFieldForm((p) => ({ ...p, label: e.target.value }))} />
                      </div>
                    </div>
                    <div className="afm-row-2 afm-form-group">
                      <div>
                        <label className="afm-label">Field Type</label>
                        <select className="afm-select" value={fieldForm.fieldType}
                          onChange={(e) => setFieldForm((p) => ({ ...p, fieldType: e.target.value }))}>
                          {FIELD_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="afm-label">Sort Order</label>
                        <input type="number" className="afm-input" value={fieldForm.sortOrder}
                          onChange={(e) => setFieldForm((p) => ({ ...p, sortOrder: +e.target.value }))} />
                      </div>
                    </div>
                    <div className="afm-form-group">
                      <label
                        className="afm-check-wrap"
                        htmlFor="reqCheck"
                        style={{ userSelect: "none" }}
                      >
                        <input type="checkbox" id="reqCheck"
                          checked={fieldForm.required}
                          onChange={(e) => setFieldForm((p) => ({ ...p, required: e.target.checked }))} />
                        <span className="afm-check-label">
                          <i className="bi bi-asterisk me-1" style={{ fontSize: 11, color: "var(--danger)" }}></i>
                          Mark this field as required
                        </span>
                      </label>
                    </div>

                    {fieldForm.fieldType === "select" && (
                      <div className="afm-form-group">
                        <label className="afm-label">
                          Options <span style={{ textTransform: "none", fontWeight: 400, color: "var(--text-light)" }}>— one per line</span>
                        </label>
                        <textarea className="afm-textarea" rows={5} value={fieldForm.optionsRaw}
                          onChange={(e) => setFieldForm((p) => ({ ...p, optionsRaw: e.target.value }))}
                          placeholder={"Option A\nOption B\nOption C"} />
                        <div className="afm-hint">
                          <i className="bi bi-info-circle me-1"></i>
                          Each line becomes a selectable option in the form.
                        </div>
                      </div>
                    )}

                    <div className="afm-sep" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="afm-btn afm-btn-primary" type="submit" disabled={submitting}>
                        <i className={`bi ${fieldEditId ? "bi-check2" : "bi-plus-lg"}`}></i>
                        {submitting ? "Saving…" : fieldEditId ? "Update Field" : "Create Field"}
                      </button>
                      <button className="afm-btn afm-btn-ghost" type="button"
                        onClick={() => {
                          setFieldEditId(null);
                          setSelectedCatId("");
                          setSelectedSubId("");
                          setFieldForm({ fieldName: "", label: "", fieldType: "text", required: false, sortOrder: 0, subCategoryId: "", optionsRaw: "" });
                        }}>
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* List — scrollable */}
              <div className="afm-card">
                <div className="afm-card-header" style={{ flexWrap: "wrap", gap: 10 }}>
                  <div className="afm-card-icon"><i className="bi bi-ui-checks-grid"></i></div>
                  <span className="afm-card-title">Field List</span>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 180 }}>
                      <SearchableSelect
                        options={activeCategories.map(c => ({ value: c.id, label: c.name }))}
                        value={selectedCatId}
                        onChange={(v) => { setSelectedCatId(v); setSelectedSubId(""); }}
                        placeholder="Category…"
                      />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <SearchableSelect
                        options={activeSubCategories.map(s => ({ value: s.id, label: s.name }))}
                        value={selectedSubId}
                        onChange={(v) => setSelectedSubId(v)}
                        disabled={!selectedCatId}
                        placeholder="Sub-category…"
                      />
                    </div>
                  </div>
                </div>
                <div className="afm-list-scroll">
                  {loadingFields ? (
                    <div className="afm-spinner-wrap">
                      <div className="afm-spinner" />
                      <span className="afm-spinner-text">Loading fields…</span>
                    </div>
                  ) : !selectedSubId ? (
                    <div className="afm-empty">
                      <div className="afm-empty-icon"><i className="bi bi-funnel"></i></div>
                      <p className="afm-empty-text">Select a sub-category</p>
                      <p className="afm-empty-sub">Use the filters above to load fields</p>
                    </div>
                  ) : fields.length === 0 ? (
                    <div className="afm-empty">
                      <div className="afm-empty-icon"><i className="bi bi-ui-checks"></i></div>
                      <p className="afm-empty-text">No fields defined yet</p>
                      <p className="afm-empty-sub">Add your first field using the form</p>
                    </div>
                  ) : (
                    <table className="afm-table">
                      <thead>
                        <tr>
                          <th>Sr.no</th>
                          <th>Field Key</th>
                          <th>Label</th>
                          <th>Type</th>
                          <th>Required</th>
                          <th>Visible</th>
                          <th>Options</th>
                          <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((f, i) => (
                          <tr key={f.id} style={{ opacity: f.active ? 1 : 0.45 }}>
                            <td><span className="afm-row-num">{i + 1}</span></td>
                            <td>
                              <span className="afm-code" style={{ textDecoration: f.active ? "none" : "line-through" }}>
                                {f.fieldName}
                              </span>
                            </td>
                            <td style={{ fontWeight: 500, textDecoration: f.active ? "none" : "line-through" }}>{f.label}</td>
                            <td>
                              <span className="afm-badge afm-badge-type">
                                <i className={`bi ${typeIcon(f.fieldType)}`}></i>
                                {f.fieldType}
                              </span>
                            </td>
                            <td>
                              {f.required
                                ? <span className="afm-badge afm-badge-req"><i className="bi bi-check-circle-fill"></i> Yes</span>
                                : <span style={{ color: "var(--text-light)", fontSize: 13 }}>No</span>}
                            </td>
                            <td>
                              <button
                                className={`afm-action-btn afm-action-toggle${f.active ? "-on" : "-off"}`}
                                onClick={() => toggleFieldActive(f)}
                                title={f.active ? "Hide from form" : "Show in form"}
                              >
                                <i className={`bi ${f.active ? "bi-eye-fill" : "bi-eye-slash-fill"}`}></i>
                              </button>
                            </td>
                            <td>
                              <span className="afm-options-preview">
                                {(f.options || []).map((o) => o.optionValue).join(", ") || <span style={{ color: "var(--text-light)" }}>—</span>}
                              </span>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <button className="afm-action-btn afm-action-edit me-1" onClick={() => editField(f)} title="Edit">
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="afm-action-btn afm-action-del" onClick={() => deleteFieldPermanently(f)} title="Delete permanently">
                                <i className="bi bi-trash3"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
